/**
 * Device-passkey (WebAuthn) client.
 *
 * A device passkey is a second, device-bound authorizer for signing a
 * transaction, layered on top of the OAuth session. The private key never
 * leaves the authenticator (a platform biometric or a security key); this module
 * only drives the browser WebAuthn ceremonies and relays the results to the
 * gateway, which is the verifier. It holds no key and makes no authorization
 * decision of its own.
 *
 * Three flows live here. Enrollment registers this device as a credential.
 * Listing and removal manage the caller's enrolled credentials. Per-transaction
 * assertion signs a challenge the gateway binds to the exact transaction being
 * authorized, producing the stamp the approve call carries in X-Device-Assertion.
 *
 * The gateway sends challenges as hex, speaks base64url for credential ids, and
 * reads the assertion's authenticator data and signature as hex with the client
 * data as a raw JSON string; the conversions live in webauthnCodec. Every ceremony is
 * fail-closed: a user cancellation, an absent authenticator, or a malformed
 * server value surfaces as a clear error rather than silently proceeding, so a
 * transaction is never signed on a failed or skipped device check.
 */

import { request, ApiError } from './api';
import type { LoginSession } from './session';
import { bytesToBase64Url, base64UrlToBytes, bytesToHex, hexToBytes } from './webauthnCodec';

/**
 * Whether this browser exposes the WebAuthn platform API. Every entry point
 * checks this first so the enroll UI can hide itself and the send path can skip
 * the device step where the API is unavailable, rather than throwing on a
 * missing global.
 *
 * @returns True when navigator.credentials and PublicKeyCredential are present.
 */
export function isWebAuthnAvailable(): boolean {
  return (
    typeof window !== 'undefined' &&
    typeof window.PublicKeyCredential !== 'undefined' &&
    typeof navigator !== 'undefined' &&
    typeof navigator.credentials !== 'undefined' &&
    typeof navigator.credentials.create === 'function' &&
    typeof navigator.credentials.get === 'function'
  );
}

/**
 * The relying party id to run a ceremony under, given the one the gateway issued.
 *
 * WebAuthn requires `rp.id` to equal the page's effective domain or be a
 * registrable suffix of it. The gateway hands out its PRIMARY relying party —
 * `plantmewallet.com` — which governs every deployed host but does NOT govern
 * `localhost`, so on a dev server the browser rejects the ceremony outright with
 * a SecurityError before any request is made. Substituting `localhost` is what
 * makes passkeys usable in local development, and mirrors what p2p does.
 *
 * KEYED ON THE LIVE HOSTNAME, DELIBERATELY. Not on `import.meta.env.DEV`, which
 * is true for any dev build including one served from a real host — that would
 * let the override reach a deployed origin. Because the branch reads
 * `location.hostname`, it is unreachable anywhere but a localhost page: shipping
 * this to production cannot change production behaviour.
 *
 * `127.0.0.1` is intentionally NOT overridden. An RP ID must be a domain, and an
 * IP literal is not one, so no value makes a ceremony work from that origin;
 * sending `localhost` from a `127.0.0.1` page would fail the browser's own
 * registrable-suffix check with a more confusing error than the honest one. Use
 * `http://localhost:5173`.
 *
 * REQUIRES MATCHING GATEWAY CONFIG. `localhost` must be in
 * `PLANTME_WEBAUTHN_RP_ID` and the dev origin in `PLANTME_WEBAUTHN_ORIGINS`, or
 * the gateway rejects the ceremony it was never told to serve.
 *
 * Note the consequence: the gateway records the relying party a credential was
 * enrolled under, so a passkey created here is a `localhost` passkey and will not
 * work on a deployed host. That is correct WebAuthn behaviour — local dev needs
 * its own enrolled passkey — not a bug to work around.
 *
 * @param serverRpId The `rp_id` from the gateway challenge response.
 * @returns The relying party id to put in the ceremony options.
 */
export function effectiveRpId(serverRpId: string): string {
  // `window.location` is read defensively, not just `window`: a partial window
  // shim (a test harness, an SSR stub, some embedded webviews) has the global but
  // no location, and dereferencing it there would throw out of every ceremony
  // rather than falling back. Anything that is not a confirmed localhost page
  // keeps the gateway's own value.
  return typeof window !== 'undefined' && window.location?.hostname === 'localhost'
    ? 'localhost'
    : serverRpId;
}

/** An enrolled device credential as the gateway lists it. */
export interface DeviceCredential {
  /** Server-side credential record id, used for removal. */
  id: string;
  /** The name the gateway assigned this method, e.g. `Willow (4821)`. */
  label: string;
  /** Unix seconds the credential was enrolled, or null when unknown. */
  createdAt: number | null;
  /** Unix seconds the credential last authorized a transaction, or null. */
  lastUsedAt: number | null;
}

/** The gateway registration-challenge response. */
interface RegisterChallengeResponse {
  /** The registration challenge, hex-encoded. */
  challenge: string;
  /** The relying-party id the credential is scoped to. */
  rp_id: string;
  /** The caller's user id, used as the WebAuthn user handle. */
  user_id: string;
  /**
   * The name the gateway has assigned this credential, e.g. `Willow (4821)`.
   *
   * Used as the ceremony's `user.name`, so the operating system's passkey picker
   * shows exactly the string the server stores as the credential's label. Server
   * chosen: WebAuthn cannot rename a credential afterwards, so letting the client
   * decide would risk the app showing a name the authenticator never had.
   */
  user_name: string;
  /** How long the browser prompt should stay open, in milliseconds. */
  timeout_ms?: number;
}

/** The gateway record returned after a successful enrollment. */
interface EnrolledCredentialResponse {
  id: string;
  label: string;
  /** Unix seconds the credential was enrolled. */
  created_at?: number | null;
}

/** One credential record as the gateway lists it. */
interface ListedCredentialResponse {
  id: string;
  label: string;
  /** Unix seconds the credential was enrolled. */
  created_at?: number | null;
  /** Unix seconds the credential last authorized a transaction. */
  last_used_at?: number | null;
}

/** The gateway per-transaction sign-challenge response. */
interface SignChallengeResponse {
  /** The transaction-bound challenge, hex-encoded. */
  challenge: string;
  /** The relying-party id to assert against. */
  rp_id: string;
  /** The caller's enrolled credential ids, base64url, for allowCredentials. */
  credential_ids: string[];
}

/**
 * The assertion parts the approve call sends in X-Device-Assertion. The header
 * value is the base64 of the JSON of this object. The parts carry the encodings
 * the gateway verifier reads: the credential id as base64url, the client data as
 * the raw UTF-8 JSON string the authenticator produced, and the authenticator
 * data and signature as lowercase hex.
 */
export interface DeviceAssertion {
  /** The credential id, base64url. */
  credential_id: string;
  /** The raw UTF-8 clientDataJSON string the gateway parses as JSON directly. */
  client_data_json: string;
  /** The authenticator data bytes, lowercase hex. */
  authenticator_data: string;
  /** The assertion signature bytes, lowercase hex. */
  signature: string;
}

/**
 * The gateway login-challenge response. The passkey login challenge carries no
 * user context: the credential is discoverable, so the browser resolves it from
 * the device without the caller naming a user first. The relying-party id scopes
 * which credentials the authenticator will offer.
 */
interface LoginChallengeResponse {
  /** The login challenge, hex-encoded. */
  challenge: string;
  /** The relying-party id to assert against. */
  rp_id: string;
  /** How long the browser prompt should stay open, in milliseconds. */
  timeout_ms?: number;
}

/**
 * The assertion parts a passkey sign-in sends to the login endpoint. These carry
 * the same encodings the signing stamp uses so the gateway verifier reads them
 * the same way: the credential id and the optional user handle as base64url, the
 * client data as the raw UTF-8 JSON string the authenticator produced, and the
 * authenticator data and signature as lowercase hex. The user handle is present
 * only when the authenticator returns one; the gateway matches it against the
 * credential's stored user id as defense in depth.
 */
export interface PasskeyLoginBody {
  /** The credential id, base64url. */
  credential_id: string;
  /** The raw UTF-8 clientDataJSON string the gateway parses as JSON directly. */
  client_data_json: string;
  /** The authenticator data bytes, lowercase hex. */
  authenticator_data: string;
  /** The assertion signature bytes, lowercase hex. */
  signature: string;
  /** The user handle the authenticator returned, base64url, when present. */
  user_handle?: string;
}

/**
 * The gateway session response a passkey sign-in returns. It is the same JWT-pair
 * shape the OAuth login returns, so the caller records it through the same
 * storeLogin seam. The org id is the personal org the gateway resolved for the
 * credential's user; a session id is metadata.
 */
interface PasskeyLoginResponse {
  access_token: string;
  refresh_token: string;
  session_id?: string;
  org_id?: string;
}

/**
 * Turn a gateway passkey response into a session, validating it first.
 *
 * This is a trust boundary: `request<T>` types the parsed JSON but does not check it,
 * so without this the declared shape is an assumption rather than a fact. The failure
 * it prevents is specific and bad. `storeLogin` sets `realSession = true` before it
 * reads the token, and `syncAccount` reports the user as authenticated on that flag
 * alone — so a 200 carrying no usable `access_token` signs the user into a shell that
 * holds no credential, where every subsequent gateway call fails and no error screen
 * ever appears. Throwing here instead surfaces the normal signup/sign-in error and
 * leaves the session untouched.
 *
 * Both tokens must be non-empty strings; the optional fields are accepted only as
 * strings and otherwise dropped, since a wrong type there would be persisted into
 * session metadata. `provider` is ours, not the gateway's, so it is set by the caller.
 *
 * @throws {Error} When the response cannot be trusted to represent a real session.
 */
function toLoginSession(raw: unknown): Omit<LoginSession, 'provider'> {
  const body = raw as Partial<PasskeyLoginResponse> | null | undefined;
  const str = (v: unknown): string | undefined => (typeof v === 'string' ? v : undefined);

  const accessToken = str(body?.access_token)?.trim();
  const refreshToken = str(body?.refresh_token)?.trim();
  if (!accessToken || !refreshToken) {
    throw new Error('The sign-in service returned an incomplete session.');
  }
  return {
    accessToken,
    refreshToken,
    sessionId: str(body?.session_id),
    orgId: str(body?.org_id) ?? '',
  };
}

/**
 * A device ceremony that the user cancelled or that found no usable
 * authenticator. Carried as its own type so the send path can surface a calm,
 * specific message and, under the additive rollout, never fall back to signing
 * without the stamp when the user actively declined.
 *
 * The noCredential flag narrows the cancelled case to the sub-case where the
 * device offered no usable passkey at all, as opposed to the user dismissing a
 * prompt that did have one. Sign-in reads it to steer a user who has no account
 * yet toward signing up rather than showing a dead-end error.
 */
export class DeviceCeremonyError extends Error {
  /** True when the user dismissed the prompt or no authenticator was available. */
  cancelled: boolean;

  /** True specifically when the device held no usable credential to sign in with. */
  noCredential: boolean;

  constructor(message: string, cancelled: boolean, noCredential = false) {
    super(message);
    this.name = 'DeviceCeremonyError';
    this.cancelled = cancelled;
    this.noCredential = noCredential;
  }
}

/**
 * Whether a passkey sign-in failed because there is no matching account for the
 * assertion, as opposed to a transport or programming error. The signup path is
 * offered in that case so a user without an account is not left at a dead end.
 *
 * A gateway that finds no credential for the presented assertion answers the
 * login with a 401 (an unauthenticated login that resolved no identity) or a 404
 * (no such credential), so those two are treated as "no account here yet". A
 * DeviceCeremonyError carrying noCredential is the client-side analog, where the
 * device itself offered no discoverable passkey.
 *
 * @param err The error thrown by {@link loginWithPasskey}.
 * @returns True when signing up is the right next step rather than a retry.
 */
export function isNoPasskeyAccount(err: unknown): boolean {
  if (err instanceof DeviceCeremonyError) return err.noCredential;
  if (err instanceof ApiError) return err.status === 401 || err.status === 404;
  return false;
}

/**
 * Whether a thrown WebAuthn error is a user cancellation or a missing
 * authenticator, as opposed to a programming or transport error. The platform
 * reports a dismissed prompt, no available authenticator, and an authenticator
 * that cannot satisfy the required user verification all as NotAllowedError;
 * AbortError covers a timed-out or aborted prompt. Each means the user did not
 * complete the ceremony, which the caller treats as a decline rather than a hard
 * failure, and reports with a clear message rather than signing silently.
 *
 * @param err The error thrown by navigator.credentials.
 * @returns True when the ceremony was declined rather than failing.
 */
function isDeclined(err: unknown): boolean {
  if (typeof DOMException !== 'undefined' && err instanceof DOMException) {
    return err.name === 'NotAllowedError' || err.name === 'AbortError';
  }
  return false;
}



/**
 * Build the create-ceremony options for enrolling a device credential. Kept as a
 * pure function of the gateway challenge so the option values, in particular the
 * required user verification that gates enrollment behind a biometric or PIN, can
 * be asserted in a unit test without driving a real authenticator.
 *
 * @param challenge The gateway registration-challenge response.
 * @returns The publicKey options for navigator.credentials.create.
 */
export function buildRegisterOptions(
  challenge: RegisterChallengeResponse,
): PublicKeyCredentialCreationOptions {
  return {
    challenge: hexToBytes(challenge.challenge),
    rp: { id: effectiveRpId(challenge.rp_id), name: 'PlantMe Wallet' },
    user: {
      // The user handle is the caller's user id UUID as raw bytes, which is what
      // the gateway keys the credential against and later matches against the
      // user_handle a usernameless sign-in returns. It is a stable, non-PII value
      // taken straight from the challenge response so it is exactly what the
      // gateway will compare, rather than the email or another display value.
      id: new TextEncoder().encode(challenge.user_id),
      name: challenge.user_name,
      displayName: challenge.user_name,
    },
    pubKeyCredParams: [
      // ES256 (P-256) first, then Ed25519 and RS256, matching the algorithms the
      // gateway's verifier accepts.
      { type: 'public-key', alg: -7 },
      { type: 'public-key', alg: -8 },
      { type: 'public-key', alg: -257 },
    ],
    timeout: challenge.timeout_ms ?? 60000,
    authenticatorSelection: {
      // Require a resident (discoverable) credential bound to this device so it
      // can be offered for usernameless sign-in later, where the browser resolves
      // the credential from the device without the caller naming it first. Also
      // require user verification so enrollment always captures a biometric or
      // PIN. The credential authorizes fund movement, so a presence-only tap is
      // not enough; an authenticator that cannot verify the user is refused here
      // rather than enrolled as a weaker factor.
      residentKey: 'required',
      userVerification: 'required',
    },
    attestation: 'none',
    // PRF capability probe. Requesting it at CREATION is the only way to learn
    // whether this authenticator can derive a stable secret (CTAP hmac-secret) for
    // the credential — a later assertion cannot retrofit the capability onto a
    // credential that was made without it.
    //
    // Nothing consumes the answer yet. It is requested now so that credentials
    // enrolled from today are already usable when client-held key material lands,
    // instead of needing every user to re-enrol. An empty `prf: {}` asks only "can
    // you?" and evaluates nothing, so the ceremony is otherwise unchanged.
    //
    // An authenticator that does not recognise the extension MUST ignore it per
    // the WebAuthn spec, which is why this is safe to request unconditionally —
    // but that is spec behaviour, not something confirmed on real hardware here.
    extensions: { prf: {} },
  };
}

/**
 * Build the get-ceremony options for a per-transaction assertion. Kept as a pure
 * function of the gateway sign-challenge so the option values, in particular the
 * required user verification that gates the assertion behind a biometric or PIN,
 * can be asserted in a unit test without driving a real authenticator.
 *
 * @param challenge The gateway sign-challenge response.
 * @returns The publicKey options for navigator.credentials.get.
 */
export function buildAssertionOptions(
  challenge: SignChallengeResponse,
): PublicKeyCredentialRequestOptions {
  const allowCredentials: PublicKeyCredentialDescriptor[] = (challenge.credential_ids ?? []).map(
    (id) => ({ type: 'public-key', id: base64UrlToBytes(id) }),
  );

  return {
    challenge: hexToBytes(challenge.challenge),
    rpId: effectiveRpId(challenge.rp_id),
    allowCredentials,
    // Require user verification: the assertion authorizes fund movement, so it
    // must be gated by a biometric or PIN rather than a bare presence tap. An
    // authenticator that cannot verify the user surfaces as a declined ceremony,
    // which the caller reports clearly instead of approving without a real check.
    userVerification: 'required',
    timeout: 60000,
  };
}



/**
 * Enroll this device as a passkey credential. Requests a registration challenge,
 * runs the browser create ceremony, and submits the resulting credential to the
 * gateway, which verifies and persists it bound to the caller.
 *
 * The gateway derives the COSE public key from the attestation object, so the
 * body carries the raw attestation and client data along with the credential id
 * and sign counter. It also echoes back the hex challenge the challenge response
 * returned, unchanged, so the gateway can bind the enrollment to the challenge it
 * issued.
 *
 * @returns The enrolled credential record.
 * @throws {DeviceCeremonyError} When the user cancels or no authenticator is
 *   available.
 * @throws {Error} When WebAuthn is unavailable or the gateway rejects the
 *   enrollment.
 */
export async function registerDevice(): Promise<DeviceCredential> {
  if (!isWebAuthnAvailable()) {
    throw new Error('This browser does not support device passkeys.');
  }

  const challenge = await request<RegisterChallengeResponse>('/v1/device/credentials/challenge', {
    method: 'POST',
    body: { purpose: 'register' },
    retry: false,
  });

  const publicKey = buildRegisterOptions(challenge);

  let credential: PublicKeyCredential | null;
  try {
    credential = (await navigator.credentials.create({ publicKey })) as PublicKeyCredential | null;
  } catch (err) {
    if (isDeclined(err)) {
      throw new DeviceCeremonyError('Device enrollment was cancelled.', true);
    }
    throw err;
  }
  if (!credential) {
    throw new DeviceCeremonyError('No device credential was created.', true);
  }

  const attestation = credential.response as AuthenticatorAttestationResponse;

  const enrolled = await request<EnrolledCredentialResponse>('/v1/device/credentials', {
    method: 'POST',
    body: {
      // The SAME string handed to the authenticator, not a placeholder.
      //
      // The current gateway ignores this and stores the name it issued. An OLDER
      // gateway still trusts it — and during the deploy window that is the one
      // running. Sending a constant there would persist literal placeholder text as
      // the credential's label while the authenticator held the challenge's name, so
      // the OS picker and Settings would disagree permanently and WebAuthn cannot
      // rename a credential. Echoing the ceremony's own name makes both gateways
      // store exactly what the authenticator was told.
      label: challenge.user_name,
      credential_id: bytesToBase64Url(credential.rawId),
      client_data_json: bytesToBase64Url(attestation.clientDataJSON),
      attestation_object: bytesToBase64Url(attestation.attestationObject),
      // Echo back the exact hex challenge the challenge response returned so the
      // gateway can bind this enrollment to the challenge it issued. It is passed
      // through unchanged rather than re-encoded.
      challenge: challenge.challenge,
      sign_count: readSignCount(attestation),
      // Whether this authenticator can derive a per-credential secret (PRF /
      // CTAP hmac-secret). A CAPABILITY HINT ONLY — never a security signal.
      //
      // getClientExtensionResults() is produced by the BROWSER, not the
      // authenticator, and is not covered by the attestation signature, so the
      // gateway cannot verify it. A client that claims true only breaks its own
      // later unwrap. If this ever needs to be trustworthy, the signed source is
      // the CTAP hmac-secret flag in authData's authenticator extension outputs.
      prf_supported: readPrfSupported(credential),
    },
    retry: false,
  });

  return {
    id: enrolled.id,
    // The gateway's stored label — the name it assigned, not what we sent.
    label: enrolled.label,
    createdAt: enrolled.created_at ?? null,
    lastUsedAt: null,
  };
}

/**
 * List the caller's enrolled device credentials. Returns an empty list rather
 * than throwing when WebAuthn is unavailable, so a boot-time load on an
 * unsupported browser is a no-op.
 *
 * @param signal Optional abort signal.
 * @returns The caller's enrolled credentials, newest first as the gateway sorts.
 */
export async function listDevices(signal?: AbortSignal): Promise<DeviceCredential[]> {
  const rows = await request<ListedCredentialResponse[]>('/v1/device/credentials', {
    method: 'GET',
    signal,
  });
  if (!Array.isArray(rows)) return [];
  return rows.map((row) => ({
    id: String(row.id),
    label: String(row.label ?? ''),
    createdAt: row.created_at ?? null,
    lastUsedAt: row.last_used_at ?? null,
  }));
}

/**
 * Remove one enrolled device credential by its record id. The gateway revokes it
 * scoped to the caller, so a foreign id cannot be removed.
 *
 * @param id The credential record id from {@link listDevices}.
 * @param signal Optional abort signal.
 */
export async function removeDevice(id: string, signal?: AbortSignal): Promise<void> {
  await request(`/v1/device/credentials/${encodeURIComponent(id)}`, {
    method: 'DELETE',
    retry: false,
    signal,
  });
}

/**
 * Produce a device assertion for an approval. Fetches the transaction-bound
 * challenge, runs the browser get ceremony over it against the caller's enrolled
 * credentials, and returns the assertion parts the approve call sends in
 * X-Device-Assertion. The challenge is bound server-side to the exact
 * transaction, so the stamp authorizes that transaction alone.
 *
 * @param approvalId The approval being authorized.
 * @param signal Optional abort signal, forwarded to the challenge fetch.
 * @returns The assertion parts, encoded as the gateway verifier expects.
 * @throws {DeviceCeremonyError} When the user cancels or no authenticator is
 *   available, so the caller can fail closed rather than approve without a stamp.
 * @throws {Error} When WebAuthn is unavailable or the challenge cannot be read.
 */
export async function assertForApproval(
  approvalId: string,
  signal?: AbortSignal,
): Promise<DeviceAssertion> {
  if (!isWebAuthnAvailable()) {
    throw new Error('This browser does not support device passkeys.');
  }

  const challenge = await request<SignChallengeResponse>(
    `/v1/approvals/${encodeURIComponent(approvalId)}/sign-challenge`,
    { method: 'GET', signal },
  );

  const publicKey = buildAssertionOptions(challenge);

  let assertion: PublicKeyCredential | null;
  try {
    assertion = (await navigator.credentials.get({ publicKey })) as PublicKeyCredential | null;
  } catch (err) {
    if (isDeclined(err)) {
      throw new DeviceCeremonyError('Device approval was cancelled.', true);
    }
    throw err;
  }
  if (!assertion) {
    throw new DeviceCeremonyError('No device assertion was produced.', true);
  }

  const response = assertion.response as AuthenticatorAssertionResponse;
  return {
    credential_id: bytesToBase64Url(assertion.rawId),
    // The gateway parses the client data as JSON directly, so it is sent as the
    // raw UTF-8 string the authenticator produced rather than base64-encoded.
    client_data_json: new TextDecoder().decode(response.clientDataJSON),
    // The authenticator data and signature are read as hex by the gateway
    // verifier, so both are lowercase hex of the raw bytes.
    authenticator_data: bytesToHex(response.authenticatorData),
    signature: bytesToHex(response.signature),
  };
}

/**
 * Shape the passkey-login request body from a browser assertion. Kept as a pure
 * function of the assertion so the exact encodings the gateway verifier reads can
 * be asserted in a unit test without driving a real authenticator: the credential
 * id as base64url of the raw id, the client data as the raw UTF-8 JSON string the
 * authenticator produced (parsed as JSON directly, not base64-decoded), the
 * authenticator data and signature as lowercase hex, and the user handle as
 * base64url only when the authenticator returned one. An absent user handle omits
 * the field entirely rather than sending an empty value.
 *
 * @param assertion The credential the get ceremony produced.
 * @returns The request body for the passkey-login endpoint.
 */
export function buildPasskeyLoginBody(assertion: PublicKeyCredential): PasskeyLoginBody {
  const response = assertion.response as AuthenticatorAssertionResponse;
  const body: PasskeyLoginBody = {
    credential_id: bytesToBase64Url(assertion.rawId),
    // The gateway parses the client data as JSON directly, so it is sent as the
    // raw UTF-8 string the authenticator produced rather than base64-encoded.
    client_data_json: new TextDecoder().decode(response.clientDataJSON),
    // The authenticator data and signature are read as hex by the gateway
    // verifier, so both are lowercase hex of the raw bytes.
    authenticator_data: bytesToHex(response.authenticatorData),
    signature: bytesToHex(response.signature),
  };
  // The user handle is present for a discoverable credential and is the caller's
  // user id the gateway matches against the stored credential as defense in
  // depth. A browser or authenticator that does not return one leaves the field
  // off; the credential lookup and signature check are the real gate regardless.
  if (response.userHandle && response.userHandle.byteLength > 0) {
    body.user_handle = bytesToBase64Url(response.userHandle);
  }
  return body;
}

/**
 * Options for {@link loginWithPasskey}. Both fields are optional so existing
 * callers that pass nothing keep today's modal behavior unchanged.
 */
export interface PasskeyLoginOptions {
  /**
   * The WebAuthn credential mediation. Omitted (the default), the browser shows
   * its modal picker, which is the tap-to-sign-in path. Set to 'conditional' to
   * run the ceremony as background conditional UI, where the browser surfaces
   * matching passkeys through the identifier field's autofill rather than a modal;
   * that call stays pending until the user picks a passkey or the signal aborts.
   */
  mediation?: CredentialMediationRequirement;
  /**
   * An abort signal that cancels an in-flight ceremony. The conditional-UI path
   * holds a long-lived get ceremony, so the caller passes a controller's signal
   * to tear it down before starting the modal path, preventing two live ceremonies
   * from racing.
   */
  signal?: AbortSignal;
}

/**
 * Sign in with a device passkey, usernameless. Requests an unauthenticated login
 * challenge, runs the browser get ceremony over it against any discoverable
 * credential on this device, and submits the resulting assertion to the gateway,
 * which resolves the credential globally, verifies the signature against the
 * stored key, and mints a session.
 *
 * The challenge carries no user context and allowCredentials is empty, so the
 * browser lets the user pick from the discoverable credentials the device holds
 * rather than the caller naming one. The gateway returns the same JWT-pair shape
 * the OAuth login returns, mapped here into the session record the caller stores
 * through the shared storeLogin seam so a passkey sign-in resumes exactly like an
 * OAuth one.
 *
 * With no options the ceremony runs modally, the tap-to-sign-in path. With
 * mediation 'conditional' and an abort signal it runs as background conditional
 * UI, resolving only once the user picks a passkey from the identifier field's
 * autofill; both paths shape and submit the same login body, so the two entry
 * points converge on one session.
 *
 * @param options Optional mediation and abort signal. Omitted, the default modal
 *   behavior is unchanged.
 * @returns The session record to hand to storeLogin.
 * @throws {DeviceCeremonyError} When the user cancels or no credential is
 *   available, so the caller can surface a calm message and leave no half-open
 *   session.
 * @throws {Error} When WebAuthn is unavailable or the gateway rejects the login.
 */
export async function loginWithPasskey(options: PasskeyLoginOptions = {}): Promise<LoginSession> {
  if (!isWebAuthnAvailable()) {
    throw new Error('This browser does not support passkeys.');
  }

  // The challenge is unauthenticated: there is no session yet, so this is fetched
  // with auth off, which also keeps the token-refresh flow from running for a
  // caller who is signed out.
  const challenge = await request<LoginChallengeResponse>('/v1/auth/passkey/challenge', {
    method: 'POST',
    auth: false,
    body: {},
    retry: false,
  });

  const publicKey: PublicKeyCredentialRequestOptions = {
    challenge: hexToBytes(challenge.challenge),
    rpId: effectiveRpId(challenge.rp_id),
    // Empty allowCredentials asks the browser to offer any discoverable
    // credential on the device, which is what makes sign-in usernameless.
    allowCredentials: [],
    // Require user verification: sign-in must be gated by a biometric or PIN
    // rather than a bare presence tap. An authenticator that cannot verify the
    // user surfaces as a declined ceremony, reported clearly to the caller.
    userVerification: 'required',
    timeout: challenge.timeout_ms ?? 60000,
  };

  // Assemble the credential request. mediation and signal are threaded through
  // only when supplied so a plain modal call passes the same options it always
  // has. The conditional path passes both: 'conditional' makes the browser offer
  // matching passkeys through the identifier field's autofill, and the signal lets
  // the caller abort that long-lived ceremony before starting a modal one.
  const getOptions: CredentialRequestOptions = { publicKey };
  if (options.mediation) getOptions.mediation = options.mediation;
  if (options.signal) getOptions.signal = options.signal;

  let assertion: PublicKeyCredential | null;
  try {
    assertion = (await navigator.credentials.get(getOptions)) as PublicKeyCredential | null;
  } catch (err) {
    if (isDeclined(err)) {
      throw new DeviceCeremonyError('Passkey sign-in was cancelled.', true);
    }
    throw err;
  }
  if (!assertion) {
    // The device resolved no discoverable credential to sign in with. Flagged as a
    // no-credential decline so the caller can offer signing up rather than a bare
    // error, since a user with no passkey here has no account to sign in to yet.
    throw new DeviceCeremonyError('No passkey was available to sign in with.', true, true);
  }

  const login = await request<unknown>('/v1/auth/passkey/login', {
    method: 'POST',
    auth: false,
    body: buildPasskeyLoginBody(assertion),
    retry: false,
  });

  // Map the gateway response into the session record the caller stores. The org
  // id is the personal org the gateway resolved for this credential's user; the
  // provider is recorded as "passkey" so the session metadata reflects how the
  // user signed in.
  return { ...toLoginSession(login), provider: 'passkey' };
}

/**
 * The gateway signup-challenge response. Like the login challenge it carries no
 * user context: signup creates a brand-new account, so the gateway derives the
 * identity from the credential the browser creates rather than from any value the
 * client names first. The relying-party id scopes the credential to this origin.
 */
interface SignupChallengeResponse {
  /** The registration challenge, hex-encoded. */
  challenge: string;
  /** The relying-party id the credential is scoped to. */
  rp_id: string;
  /** How long the browser prompt should stay open, in milliseconds. */
  timeout_ms?: number;
  /**
   * The name the gateway has assigned this credential, e.g. `Willow (4821)`.
   *
   * Relayed as the ceremony's `user.name` so the OS picker and the app agree.
   * Absent on a gateway that predates naming, in which case the old generic label
   * is kept rather than inventing one the server will not have stored.
   */
  user_name?: string;
}

/**
 * The signup request body a passkey signup sends to the gateway. It is a create
 * ceremony, so it carries the same encodings enrollment uses: the credential id
 * as base64url of the raw id, the raw clientDataJSON and attestationObject as
 * base64url of their bytes, the hex challenge echoed back unchanged so the
 * gateway binds the signup to the challenge it issued, and the authenticator's
 * sign counter. The optional label is IGNORED by the gateway, which stores the name
 * it issued with the challenge; it is omitted entirely when the caller passes none
 * rather than sent empty.
 */
export interface PasskeySignupBody {
  /**
   * Ignored by the gateway, which stores the name it issued with the challenge. A
   * name is baked into the credential during the ceremony and WebAuthn cannot rename
   * one, so only the server may choose it. Retained because the field still exists.
   */
  label?: string;
  /** The credential id, base64url. */
  credential_id: string;
  /** The raw clientDataJSON bytes, base64url. */
  client_data_json: string;
  /** The raw attestationObject bytes, base64url. */
  attestation_object: string;
  /** The hex challenge the challenge response returned, echoed unchanged. */
  challenge: string;
  /** The authenticator sign counter, or zero when the browser hides it. */
  sign_count: number;
  /** Browser-reported PRF capability. Hint only; the gateway cannot verify it. */
  prf_supported: boolean;
}

/**
 * Shape the passkey-signup request body from a browser create-ceremony
 * credential. Kept as a pure function of the credential and the echoed hex
 * challenge so the exact encodings the gateway verifier reads can be asserted in
 * a unit test without driving a real authenticator: the credential id as
 * base64url of the raw id, the client data and attestation object as base64url of
 * their raw bytes, the challenge passed through byte-for-byte, and the sign
 * counter read from the attestation. The label is included only when a non-empty
 * one is supplied, so a blank label omits the field rather than sending it empty.
 *
 * @param credential The credential the create ceremony produced.
 * @param challengeHex The hex challenge from the signup-challenge response.
 * @param label Ignored by the gateway, which stores the name it issued with the
 *   challenge. Retained only because the request field still exists.
 * @returns The request body for the passkey-signup endpoint.
 */
export function buildSignupBody(
  credential: PublicKeyCredential,
  challengeHex: string,
  label?: string,
): PasskeySignupBody {
  const attestation = credential.response as AuthenticatorAttestationResponse;
  const body: PasskeySignupBody = {
    credential_id: bytesToBase64Url(credential.rawId),
    // The gateway derives the COSE public key from the attestation object and
    // reads the client data from its raw bytes, so both ride as base64url of the
    // bytes the authenticator produced, matching the enrollment path.
    client_data_json: bytesToBase64Url(attestation.clientDataJSON),
    attestation_object: bytesToBase64Url(attestation.attestationObject),
    // Echo the exact hex challenge the challenge response returned so the gateway
    // can bind this signup to the challenge it issued. It is passed through
    // unchanged rather than re-encoded.
    challenge: challengeHex,
    sign_count: readSignCount(attestation),
    // Capability hint, never a security signal — see readPrfSupported.
    prf_supported: readPrfSupported(credential),
  };
  const trimmed = label?.trim();
  if (trimmed) body.label = trimmed;
  return body;
}

/**
 * Create a new account from a passkey, passwordless and unauthenticated. Requests
 * a signup challenge, runs the browser create ceremony to mint a fresh
 * discoverable credential, and submits it to the gateway, which derives a
 * deterministic identity from the credential, provisions a personal org, persists
 * the credential, and mints a session.
 *
 * The user handle is a fresh random 16 bytes rather than a real identifier: at
 * signup there is no account yet, and the gateway derives the account identity
 * from the credential the ceremony produces, not from this handle. The gateway
 * returns the same JWT-pair shape the OAuth login returns, mapped here into the
 * session record the caller stores through the shared storeLogin seam so a passkey
 * signup signs the user in exactly like an OAuth sign-in.
 *
 * @param label Ignored by the gateway, which stores the name it issued with the
 *   challenge. Retained only because the request field still exists.
 * @returns The session record to hand to storeLogin.
 * @throws {DeviceCeremonyError} When the user cancels or no authenticator is
 *   available, so the caller can surface a calm message and leave no half-open
 *   session.
 * @throws {ApiError} When the gateway rejects the signup, for example a 409 when
 *   this credential already has an account, which the caller steers to sign-in.
 * @throws {Error} When WebAuthn is unavailable.
 */
export async function signupWithPasskey(label?: string): Promise<LoginSession> {
  if (!isWebAuthnAvailable()) {
    throw new Error('This browser does not support passkeys.');
  }

  // The challenge is unauthenticated: there is no session yet, so this is fetched
  // with auth off, which also keeps the token-refresh flow from running for a
  // signed-out caller.
  const challenge = await request<SignupChallengeResponse>('/v1/auth/passkey/signup/challenge', {
    method: 'POST',
    auth: false,
    body: {},
    retry: false,
  });

  // A fresh random user handle. The gateway derives the account identity from the
  // credential it verifies, not from this value, so a random 16 bytes is a valid
  // non-PII handle for a create with no account behind it yet.
  const userHandle = new Uint8Array(16);
  if (typeof crypto !== 'undefined' && typeof crypto.getRandomValues === 'function') {
    crypto.getRandomValues(userHandle);
  }

  const publicKey: PublicKeyCredentialCreationOptions = {
    challenge: hexToBytes(challenge.challenge),
    rp: { id: effectiveRpId(challenge.rp_id), name: 'PlantMe Wallet' },
    user: {
      id: userHandle,
      // The gateway's assigned name, so the OS picker and the app agree. WebAuthn
      // cannot rename a credential afterwards, which is why the server decides this
      // and the client only relays it. An older gateway sends nothing, and the
      // previous generic label is kept rather than inventing a name the server will
      // not have stored.
      name: challenge.user_name ?? 'passkey',
      displayName: challenge.user_name ?? 'Passkey account',
    },
    pubKeyCredParams: [
      // ES256 (P-256) first, then Ed25519 and RS256, matching the algorithms the
      // gateway's verifier accepts.
      { type: 'public-key', alg: -7 },
      { type: 'public-key', alg: -8 },
      { type: 'public-key', alg: -257 },
    ],
    timeout: challenge.timeout_ms ?? 60000,
    authenticatorSelection: {
      // Require a resident (discoverable) credential so the new account can sign in
      // usernameless later, and require user verification so signup captures a
      // biometric or PIN rather than a bare presence tap.
      residentKey: 'required',
      userVerification: 'required',
    },
    attestation: 'none',
    // PRF capability probe, same as enrollment — and this is the path that matters
    // most. Signup creates a user's FIRST passkey, so an account whose only
    // credential was made here would otherwise never record the capability, which
    // is exactly the re-enrolment campaign the probe exists to avoid.
    //
    // This ceremony deliberately builds its own options rather than reusing
    // buildRegisterOptions (different challenge type, different user handle), which
    // is how the extension was missed here first time round. Keep the two in step.
    extensions: { prf: {} },
  };

  let credential: PublicKeyCredential | null;
  try {
    credential = (await navigator.credentials.create({ publicKey })) as PublicKeyCredential | null;
  } catch (err) {
    if (isDeclined(err)) {
      throw new DeviceCeremonyError('Passkey signup was cancelled.', true);
    }
    throw err;
  }
  if (!credential) {
    throw new DeviceCeremonyError('No passkey was created.', true);
  }

  const signup = await request<unknown>('/v1/auth/passkey/signup', {
    method: 'POST',
    auth: false,
    body: buildSignupBody(credential, challenge.challenge, label),
    retry: false,
  });

  // Map the gateway response into the session record the caller stores, exactly
  // as the sign-in path does. The provider is recorded as "passkey" so the session
  // metadata reflects how the user is signed in; the caller separately flags this
  // as a fresh signup. That records provenance; the create-a-passkey offer is
  // already gated off by provider === 'passkey' leaving freshLogin false.
  return { ...toLoginSession(signup), provider: 'passkey' };
}

/**
 * Serialize a device assertion into the X-Device-Assertion header value: the
 * base64 of the JSON of the assertion parts, matching what the gateway decodes.
 *
 * @param assertion The assertion parts from {@link assertForApproval}.
 * @returns The base64-encoded JSON to place in the header.
 */
export function encodeAssertionHeader(assertion: DeviceAssertion): string {
  const json = JSON.stringify(assertion);
  const bytes = new TextEncoder().encode(json);
  // The header is standard base64, not base64url: it is an opaque byte blob the
  // gateway base64-decodes, and standard base64 is the conventional choice for
  // that container.
  let binary = '';
  const chunk = 0x8000;
  for (let i = 0; i < bytes.length; i += chunk) {
    binary += String.fromCharCode(...bytes.subarray(i, i + chunk));
  }
  return btoa(binary);
}

/**
 * Read the authenticator sign counter from an attestation, when the browser
 * exposes it. The counter feeds the gateway's clone detection. A browser that
 * does not expose getAuthenticatorData yields zero, which the gateway treats as
 * an unknown starting counter.
 *
 * @param attestation The attestation response.
 * @returns The sign counter, or zero when it cannot be read.
 */
/**
 * Whether the authenticator reported PRF support for the credential just created.
 *
 * The shape is `{ prf: { enabled: boolean } }`, but every part of it is optional in
 * practice: browsers without the extension return no `prf` key at all, and some
 * return `prf` with no `enabled`. Anything other than an explicit `true` is read as
 * "no", so an unknown browser degrades to the same answer as an unsupported
 * authenticator rather than throwing mid-enrolment.
 *
 * Never let this throw: it runs AFTER the credential exists, so a failure here
 * would lose a successful enrolment over a capability hint.
 *
 * @param credential The freshly created credential.
 * @returns True only when the browser explicitly reported PRF as enabled.
 */
function readPrfSupported(credential: PublicKeyCredential): boolean {
  try {
    const results = credential.getClientExtensionResults() as {
      prf?: { enabled?: boolean };
    };
    return results?.prf?.enabled === true;
  } catch {
    return false;
  }
}

function readSignCount(attestation: AuthenticatorAttestationResponse): number {
  try {
    const getData = (
      attestation as unknown as { getAuthenticatorData?: () => ArrayBuffer }
    ).getAuthenticatorData;
    if (typeof getData !== 'function') return 0;
    const data = new Uint8Array(getData.call(attestation));
    // The sign counter is a 32-bit big-endian integer at offset 33, after the
    // 32-byte rpIdHash and the 1-byte flags.
    if (data.length < 37) return 0;
    const view = new DataView(data.buffer, data.byteOffset, data.byteLength);
    return view.getUint32(33, false);
  } catch {
    return 0;
  }
}
